import React, { useContext, useState, useEffect, useRef } from "react";
import { CopilotChat } from "@copilotkit/react-ui";
import { CopilotKit, useCopilotAction } from "@copilotkit/react-core";
import "./AskAI.scss";
import { Avatar, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import AppContext from "../../config/AppContext";
import RecentAgents from "./RecentAgents";
import { ReactComponent as Emailicon } from "../../assets/images/email-icon.svg";
import { ReactComponent as Summaryicon } from "../../assets/images/summary-icon.svg";
import { ReactComponent as ChatIcon } from "../../assets/images/nav-icons/chat-icon.svg";
import { message } from "antd";
import CommonUtil from "../../util/CommonUtils";

const CustomUserMessage = (props) => {
  const [userName, setUserName] = useState("");
  const { mode } = useContext(AppContext);
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("USER"));
    if (storedUser && storedUser.firstName) {
      setUserName(getInitials(storedUser.firstName));
    }
  }, []);

  const getInitials = (name) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    const initials = parts.slice(0, 2).map((p) => p.charAt(0).toUpperCase());
    return initials.join("");
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap={2}
      mb={2}
    >
      <Box sx={{ maxWidth: "80%", wordBreak: "break-word", flexShrink: 0 }}>
        <span
          className={
            mode === "dark"
              ? "selected-question-name-dark"
              : "selected-question-name"
          }
        >
          {props.message}
        </span>
      </Box>
      <Avatar
        sx={{
          bgcolor: "#fa9c2b",
          width: 40,
          height: 40,
          fontSize: 16,
          boxShadow: 1,
        }}
      >
        {userName}
      </Avatar>
    </Box>
  );
};

const AskAI = () => {
  const { t } = useTranslation();
  const { mode } = useContext(AppContext);
  const [selectedAgentId, setSelectedAgentId] = useState("summary");
  const [hasMessages, setHasMessages] = useState(false);
  const [componentKey, setComponentKey] = useState(0);
  const chatContainerRef = useRef(null);

  const agents = [
    { id: "summary", name: "Summary Agent", icon: "/icons/summary.png" },
    { id: "email", name: "Email Agent", icon: "/icons/email.png" },
  ];

  const agentContent = {
    email: {
      heading: "Hey! I'm your smart email sidekick",
      subtext: (
        <>
          Need a quick summary, a reply draft, or help sorting out the mess in
          your inbox?
          <br />
          Just say the word — I turn email chaos into clarity in seconds.
        </>
      ),
      actionButtons1: [
        "Summarize my unread emails from today",
        "Prioritize emails by urgency and topic",
        "Create a follow-up email",
      ],
      actionButtons2: [
        "Summarize using only key decision points",
        "Draft a professional reply",
      ],
    },
    summary: {
      heading: "Hey! I'm your AI summary assistant",
      subtext: (
        <>
          I turn your daily work logs into clear, concise summaries in seconds.
          <br />
          Just pick a time range, choose your style, and let me handle the rest.
        </>
      ),
      actionButtons1: [
        "Summarize today's work log in bullet points",
        "Give me a weekly summary in a professional tone",
        "Generate a casual-style summary",
      ],
      actionButtons2: [
        "Create a summary in formal report tone with date and time entries",
        "Summarize only high-priority tasks from this week",
      ],
    },
  };

  const currentContent = agentContent[selectedAgentId] || agentContent.email;

  useCopilotAction({
    name: "getInfoAboutPerson",
    description:
      "Call the API by passing the user prompt as it is in string format and Display the response as-is without any modifications.",
    parameters: [
      {
        name: "message",
        type: "string",
        description: "User input",
      },
    ],
    handler: async ({ message }) => {
      try {
        const response = await fetch(
          "https://mcp-summarization.minervaiotstaging.com/chat",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("token"),
            },
            body: JSON.stringify({
              message: message,
              session_id:
                CommonUtil.getLoggedUser().id +
                "" +
                CommonUtil.getLoggedUser().companyId,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data.response || "No response from server";
      } catch (error) {
        console.error("Error calling API:", error);
        return "Failed to get response from server.";
      }
    },
  });

  return (
    <div className="ask-ai-container" key={componentKey}>
      <div className="task-button-alignment">
        <button
          className={
            mode === "dark" ? "new-task-buttonDark" : "new-task-button"
          }
        >
          {t("Conversations")}
        </button>
      </div>
      <div className="task-form-container">
        <div
          className={
            mode === "dark"
              ? "task-form-container2dark"
              : "task-form-container2"
          }
        >
          <RecentAgents
            agents={agents}
            selectedAgentId={selectedAgentId}
            onSelectAgent={(agentId) => {
              setSelectedAgentId(agentId);
              setComponentKey((prev) => prev + 1);
            }}
          />
        </div>
        <div
          className={
            mode === "dark"
              ? "task-form-container1dark"
              : "task-form-container1"
          }
        >
          <div className="ask-ai-inner">
            <div className="ask-ai-available">
              <div className="ask-ai-available-left">
                {selectedAgentId === "email" ? (
                  <Emailicon className="agent-icon" />
                ) : (
                  <Summaryicon className="agent-icon" />
                )}
                <span
                  className={
                    mode === "dark"
                      ? "selected-agent-name-dark"
                      : "selected-agent-name"
                  }
                >
                  {agents.find((a) => a.id === selectedAgentId).name}
                </span>
              </div>
              <div className="ask-ai-available-right">
                <span
                  className={
                    mode === "dark"
                      ? "ask-ai-available-chat-dark"
                      : "ask-ai-available-chat"
                  }
                  onClick={() => setComponentKey((prev) => prev + 1)}
                >
                  <ChatIcon /> New Chat
                </span>
              </div>
            </div>

            <div
              className={`ask-ai-intro ${hasMessages ? "hidden" : ""}`}
              style={{ display: hasMessages ? "none" : "flex" }}
            >
              <p
                className={
                  mode === "dark" ? "ask-ai-headingdark" : "ask-ai-heading"
                }
              >
                {currentContent.heading}
              </p>
              <span
                className={
                  mode === "dark" ? "ask-ai-subtext-dark" : "ask-ai-subtext"
                }
              >
                {currentContent.subtext}
              </span>
              <div
                className={
                  mode === "dark" ? "ask-ai-actions-dark" : "ask-ai-actions"
                }
              >
                {currentContent.actionButtons1.map((btn, i) => (
                  <button key={i}>{btn}</button>
                ))}
              </div>
              <div
                className={
                  mode === "dark" ? "ask-ai-actions-dark" : "ask-ai-actions"
                }
              >
                {currentContent.actionButtons2.map((btn, i) => (
                  <button key={i}>{btn}</button>
                ))}
              </div>
            </div>

            <div
              className="ask-ai-chat-wrapper"
              ref={chatContainerRef}
              onClick={() => setHasMessages(true)}
            >
              <CopilotChat
                instructions={
                  selectedAgentId === "summary"
                    ? "Always Call the API by passing the user prompt as it is in string format and Display the response as-is without any modifications."
                    : `You are an AI email assistant that helps with email management.`
                }
                labels={{
                  title: "Your Assistant",
                  initial:
                    selectedAgentId === "summary"
                      ? `Hi there I'm your AI Summary Assistant. Ask me what someone is working on.`
                      : `Hi there I'm your Email Assistant. How can I help with your emails today?`,
                  placeholder: "What can I help you with today?",
                }}
                className="copilot-chat-container"
                UserMessage={CustomUserMessage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskAI;
